import { Link } from "react-router-dom";
import { Heart, ArrowRight, Share2, Copy, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTours } from "../lib/useTours";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import TourCard from "../components/TourCard";
import { Button } from "../components/ui/button";

export default function Wishlist() {
  const { user, loading, wishlist, login } = useAuth();
  const { tours } = useTours();
  const { toast } = useToast();
  const [share, setShare] = useState(null); // {token, ...} or null
  const [shareLoading, setShareLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/wishlist/share/mine");
        if (data && data.token) setShare(data);
      } catch {}
    })();
  }, [user]);

  const shareLink = share ? `${window.location.origin}/w/${share.token}` : "";

  const createShare = async () => {
    setShareLoading(true);
    try {
      const { data } = await api.post("/wishlist/share");
      setShare(data);
      const link = `${window.location.origin}/w/${data.token}`;
      const msg = `Hey! Check out the trips I'm eyeing on On Wheels 🚐\n${link}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
    } catch {
      toast({ title: "Couldn't create share link", variant: "destructive" });
    } finally {
      setShareLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast({ title: "Link copied" });
    } catch {
      toast({ title: "Copy failed", description: shareLink });
    }
  };

  const revokeShare = async () => {
    try {
      await api.post("/wishlist/share/revoke");
      setShare(null);
      toast({ title: "Share link disabled" });
    } catch {
      toast({ title: "Couldn't disable link", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <main className="pt-32 pb-16 min-h-screen text-center">
        <div className="w-10 h-10 rounded-full border-2 border-[#1F4D3A]/20 border-t-[#1F4D3A] animate-spin mx-auto" />
      </main>
    );
  }

  if (!user) {
    return (
      <main className="pt-32 pb-24 min-h-screen paper-texture">
        <section className="max-w-2xl mx-auto text-center px-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F5F0E8] mb-6">
            <Heart className="w-7 h-7 text-[#C87A50]" />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#1F4D3A] mb-3">Your wishlist awaits.</h1>
          <p className="text-[#4A3728] text-lg mb-8">Log in to build a collection of trips you'd love to take.</p>
          <Button
            data-testid="wishlist-login-cta"
            onClick={login}
            className="btn-primary bg-[#1F4D3A] hover:bg-[#2D6B4F] text-white rounded-full px-8 h-12"
          >
            Sign in with Google <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </section>
      </main>
    );
  }

  const savedTours = tours.filter((t) => wishlist.includes(t.id));

  return (
    <main className="pt-24 sm:pt-32 pb-16 paper-texture min-h-screen">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 sm:mb-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <span className="leaf-divider inline-flex">Your journeys</span>
            <h1 className="font-serif text-3xl sm:text-5xl font-bold text-[#1F4D3A] mt-3">
              Wishlist <span className="text-[#C87A50]">·</span>{" "}
              <span className="text-[#C87A50]">{savedTours.length}</span>
            </h1>
            <p className="text-[#4A3728] mt-3 max-w-2xl">
              The trips you've hearted. Whenever you're ready, book one and we'll take it from there.
            </p>
          </div>
          {savedTours.length > 0 && (
            <div className="flex flex-col gap-2 items-stretch sm:items-end" data-testid="wishlist-share-panel">
              {!share ? (
                <Button
                  data-testid="wishlist-share-btn"
                  onClick={createShare}
                  disabled={shareLoading}
                  className="btn-primary bg-[#25D366] hover:bg-[#1EBE5D] text-white rounded-full h-11 px-5"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  {shareLoading ? "Creating…" : "Share my wishlist"}
                </Button>
              ) : (
                <div className="flex flex-col sm:items-end gap-2">
                  <div className="flex items-center gap-2 bg-white border border-[#E5DDCB] rounded-full pl-4 pr-1 py-1 max-w-full">
                    <span className="text-xs text-[#6B8E5A] uppercase tracking-widest font-semibold">Share link</span>
                    <span data-testid="wishlist-share-link" className="text-xs sm:text-sm text-[#1F4D3A] font-mono truncate max-w-[220px]">{shareLink}</span>
                    <button onClick={copyLink} aria-label="Copy link" data-testid="wishlist-copy-btn" className="w-8 h-8 rounded-full bg-[#F5F0E8] hover:bg-[#E5DDCB] flex items-center justify-center">
                      <Copy className="w-3.5 h-3.5 text-[#1F4D3A]" />
                    </button>
                  </div>
                  <div className="flex gap-2 sm:justify-end">
                    <Button
                      onClick={() => {
                        const msg = `Hey! Check out the trips I'm eyeing on On Wheels 🚐\n${shareLink}`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
                      }}
                      data-testid="wishlist-wa-share-btn"
                      className="btn-primary bg-[#25D366] hover:bg-[#1EBE5D] text-white rounded-full h-10 px-4"
                    >
                      <Share2 className="w-4 h-4 mr-2" /> WhatsApp
                    </Button>
                    <Button
                      onClick={revokeShare}
                      data-testid="wishlist-revoke-btn"
                      variant="outline"
                      className="rounded-full border-[#E5DDCB] text-[#4A3728] hover:bg-[#F5F0E8] h-10 px-4"
                    >
                      <X className="w-4 h-4 mr-2" /> Disable
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {savedTours.length === 0 ? (
          <div className="bg-white border border-[#E5DDCB] rounded-3xl p-10 sm:p-14 text-center" data-testid="wishlist-empty">
            <Heart className="w-10 h-10 text-[#C87A50] mx-auto mb-4" />
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1F4D3A] mb-2">Nothing saved yet.</h2>
            <p className="text-[#5A4A38] mb-6">Tap the heart on any tour to keep it here.</p>
            <Link to="/tours">
              <Button className="btn-primary bg-[#C87A50] hover:bg-[#B4653A] text-white rounded-full px-8 h-12">
                Explore tours <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-testid="wishlist-grid">
            {savedTours.map((t) => <TourCard key={t.id} tour={t} />)}
          </div>
        )}
      </section>
    </main>
  );
}
